// components/video/SignalOverlayViewModal.tsx
import FeatherIcon from 'feather-icons-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Col, Row, Spinner } from 'react-bootstrap';
import { ImageMarker, videoProjectService } from 'services/videoService';

interface Props {
  show: boolean;
  onHide: () => void;
  overlaySign?:
    | (ImageMarker & {
        projectId: number;
        projectName: string;
        distance: number;
        isOverlay: true;
      })
    | null;
  sign?: ImageMarker | null; // ✅ NOVO - Aceita também um sign normal (sem overlay)
  hideProjectButton?: boolean; // ✅ NOVO - Oculta botão "Ir para Projeto" (default: false)
}

// Modal Arrastável Customizado
const CustomModal: React.FC<{
  show: boolean;
  onHide: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'lg' | 'xl';
}> = ({ show, onHide, title, children, size = 'lg' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(1);
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);

  useEffect(() => {
    if (show) {
      setPosition({ x: 0, y: 0 });
      setOpacity(1);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        e.preventDefault();
        e.stopPropagation();
        onHide();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [show, onHide]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    },
    [position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!show) return null;

  const modalWidth =
    size === 'sm' ? '500px' : size === 'xl' ? '1140px' : '800px';

  return (
    <>
      <div
        className="custom-modal-backdrop"
        onClick={onHide}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
      />

      <div
        className="custom-modal"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          width: modalWidth,
          maxWidth: '90vw',
          maxHeight: isDragging ? '85vh' : '90vh',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: isDragging
            ? '0 8px 32px rgba(0,0,0,0.3)'
            : '0 4px 16px rgba(0,0,0,0.15)',
          opacity,
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column',
          transition: isDragging ? 'none' : 'box-shadow 0.2s',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem',
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <h5 style={{ margin: 0, flex: 1 }}>{title}</h5>

          <div
            className="d-flex align-items-center gap-2"
            style={{ marginRight: '8px' }}
          >
            {/* Transparência */}
            <div style={{ position: 'relative' }}>
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowOpacitySlider(!showOpacitySlider)}
                style={{ padding: '4px 8px', color: '#6c757d' }}
              >
                <FeatherIcon icon="eye" size={16} />
                <span style={{ fontSize: '11px', marginLeft: '4px' }}>
                  {Math.round(opacity * 100)}%
                </span>
              </Button>

              {showOpacitySlider && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    zIndex: 1100,
                    minWidth: '200px'
                  }}
                >
                  <input
                    type="range"
                    min="30"
                    max="100"
                    value={opacity * 100}
                    onChange={e => setOpacity(parseInt(e.target.value) / 100)}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </div>

            {/* Arrastar */}
            <div
              onMouseDown={handleMouseDown}
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
                padding: '4px 8px',
                backgroundColor: isDragging ? '#e9ecef' : '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                userSelect: 'none'
              }}
            >
              <FeatherIcon icon="move" size={14} />
              <span style={{ fontSize: '11px' }}>
                {isDragging ? 'Arrastando...' : 'Arrastar'}
              </span>
            </div>

            {/* Fechar */}
            <button
              onClick={onHide}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                padding: '0 4px',
                color: '#6c757d'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '1rem',
            overflowY: 'auto',
            flex: 1
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
};

const SignalOverlayViewModal: React.FC<Props> = ({
  show,
  onHide,
  overlaySign: overlaySignProp,
  sign: signProp,
  hideProjectButton = false // ✅ Default: mostra o botão
}) => {
  // ✅ Aceita tanto overlaySign quanto sign
  const overlaySign = overlaySignProp || (signProp as any);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);
  const [loadedImageId, setLoadedImageId] = useState<number | null>(null); // ✅ NOVO - Controla qual imagem foi carregada

  useEffect(() => {
    if (show && overlaySign) {
      setIsBlinking(true);
      const timer = setTimeout(() => setIsBlinking(false), 2000);
      return () => clearTimeout(timer);
    } else if (!show) {
      // ✅ Limpar estados quando fechar
      setImageSrc(null);
      setImageLoading(false);
      setImageError(false);
      setLoadedImageId(null);
    }
  }, [show, overlaySign]);

  useEffect(() => {
    const loadImage = async () => {
      if (!overlaySign || !overlaySign.project_image_id) {
        return;
      }

      // ✅ Evitar recarregar a mesma imagem
      if (loadedImageId === overlaySign.project_image_id && imageSrc) {
        console.log('[OverlayModal] ⏭️ Imagem já carregada, pulando...');
        return;
      }

      // ✅ Buscar projectId de várias formas possíveis
      const projectId =
        overlaySign.projectId ||
        overlaySign.project_id ||
        (overlaySign as any).signData?.projectId;

      if (!projectId) {
        console.error(
          '[OverlayModal] ❌ projectId não disponível',
          overlaySign
        );
        return;
      }

      setImageLoading(true);
      setImageError(false);

      try {
        console.log('[OverlayModal] 🔄 Carregando imagem:', {
          projectId,
          imageId: overlaySign.project_image_id
        });

        const imageUrl = await videoProjectService.getProjectImage(
          projectId,
          overlaySign.project_image_id
        );

        console.log('[OverlayModal] ✅ Imagem carregada');
        setImageSrc(imageUrl);
        setLoadedImageId(overlaySign.project_image_id); // ✅ Marca como carregada
      } catch (error) {
        console.error('[OverlayModal] ❌ Erro ao carregar imagem:', error);
        setImageError(true);
      } finally {
        setImageLoading(false);
      }
    };

    if (show && overlaySign) {
      loadImage();
    }

    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [show, overlaySign?.project_image_id, overlaySign?.projectId]); // ✅ Dependências específicas

  if (!overlaySign) return null;

  const signImagePath = overlaySign.signal_device?.symbol_name
    ? `/images/signs/${overlaySign.signal_device.symbol_name}`
    : null;

  const formatTypeName = (type: string) => {
    const typeNames: { [key: string]: string } = {
      regulamentacao: 'Regulamentação',
      advertencia: 'Advertência',
      auxiliares: 'Auxiliares',
      indicativa: 'Indicativa'
    };
    return typeNames[type] || type;
  };

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title={`Detalhes da Sinalização ${
        overlaySign.isOverlay ? '(Overlay)' : ''
      }`}
      size="lg"
    >
      <Row>
        {/* Coluna Esquerda: Informações */}
        <Col md={6}>
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-2">
              <Badge bg="secondary">
                {overlaySign.projectName || 'Projeto Principal'}
              </Badge>
              {overlaySign.distance !== undefined && (
                <Badge bg="info">
                  {overlaySign.distance < 1000
                    ? `${overlaySign.distance.toFixed(0)}m de distância`
                    : `${(overlaySign.distance / 1000).toFixed(
                        1
                      )}km de distância`}
                </Badge>
              )}
            </div>

            <h6>
              <strong>{overlaySign.signal_device?.code || 'N/A'}</strong>
            </h6>
            <p className="text-muted mb-0">
              {overlaySign.signal_device?.name || 'Nome não disponível'}
            </p>
          </div>

          {overlaySign.signal_device?.description && (
            <div className="mb-3">
              <small className="text-muted">
                <strong>Descrição:</strong>{' '}
                {overlaySign.signal_device.description}
              </small>
            </div>
          )}

          <div className="mb-3">
            <h6 className="text-muted mb-2">
              <FeatherIcon icon="info" size={16} className="me-2" />
              Detalhes
            </h6>

            <div className="row g-2">
              <div className="col-12">
                <small className="text-muted">
                  <strong>Tipo:</strong>{' '}
                  {formatTypeName(overlaySign.signal_device?.type || 'N/A')}
                </small>
              </div>

              {overlaySign.signal_service && (
                <div className="col-12">
                  <small className="text-muted">
                    <strong>Serviço:</strong> {overlaySign.signal_service.name}
                  </small>
                </div>
              )}

              {overlaySign.signal_film && (
                <div className="col-12">
                  <small className="text-muted">
                    <strong>Película:</strong> {overlaySign.signal_film.name}
                  </small>
                </div>
              )}

              {overlaySign.signal_support && (
                <div className="col-12">
                  <small className="text-muted">
                    <strong>Suporte:</strong> {overlaySign.signal_support.code}{' '}
                    - {overlaySign.signal_support.name}
                  </small>
                </div>
              )}

              {overlaySign.comments && (
                <div className="col-12">
                  <small className="text-muted">
                    <strong>Observações:</strong> {overlaySign.comments}
                  </small>
                </div>
              )}

              <div className="col-12">
                <small className="text-muted">
                  <strong>GPS:</strong> {overlaySign.lat.toFixed(6)},{' '}
                  {overlaySign.long.toFixed(6)}
                </small>
              </div>
            </div>
          </div>
        </Col>

        {/* Coluna Direita: Imagens */}
        <Col md={6}>
          {/* Símbolo da Placa */}
          <div className="mb-3">
            <h6 className="text-muted mb-2">
              <FeatherIcon icon="image" size={16} className="me-2" />
              Símbolo da Placa
            </h6>
            <div
              className="border rounded p-3 d-flex align-items-center justify-content-center"
              style={{
                minHeight: '180px',
                backgroundColor: '#f8f9fa',
                animation: isBlinking
                  ? 'pulse-symbol 0.5s ease-in-out 4'
                  : 'none'
              }}
            >
              {signImagePath ? (
                <img
                  src={signImagePath}
                  alt="Símbolo"
                  style={{
                    maxWidth: '150px',
                    maxHeight: '150px',
                    objectFit: 'contain'
                  }}
                  onError={e => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML = `
                        <div style="text-align: center; color: #6c757d;">
                          <div style="font-size: 60px;">🚦</div>
                          <small>Imagem não disponível</small>
                        </div>
                      `;
                    }
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#6c757d' }}>
                  <div style={{ fontSize: '60px' }}>🚦</div>
                  <small>Símbolo não disponível</small>
                </div>
              )}
            </div>
          </div>

          {/* Foto da Imagem */}
          <div className="mb-3">
            <h6 className="text-muted mb-2">
              <FeatherIcon icon="camera" size={16} className="me-2" />
              Foto da Captura
            </h6>
            <div
              className="border rounded p-2"
              style={{
                height: '300px',
                backgroundColor: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}
            >
              {imageLoading && (
                <div className="text-center">
                  <Spinner animation="border" variant="light" />
                  <div className="text-light mt-2">
                    <small>Carregando imagem...</small>
                  </div>
                </div>
              )}

              {imageError && !imageLoading && (
                <div className="text-center text-light">
                  <FeatherIcon icon="alert-circle" size={48} className="mb-2" />
                  <div>Erro ao carregar imagem</div>
                </div>
              )}

              {imageSrc && !imageLoading && !imageError && (
                <>
                  <img
                    src={imageSrc}
                    alt="Captura"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />

                  {/* Marcador da posição - Imagem da placa */}
                  {signImagePath ? (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${overlaySign.x_coordinate * 100}%`,
                        top: `${overlaySign.y_coordinate * 100}%`,
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        zIndex: 10,
                        filter:
                          'drop-shadow(0 0 4px rgba(255,255,255,0.8)) drop-shadow(0 2px 6px rgba(0,0,0,0.6))'
                      }}
                    >
                      <img
                        src={signImagePath}
                        alt="Marcador"
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'contain'
                        }}
                        onError={e => {
                          const parent = (e.target as HTMLElement)
                            .parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div style="
                                width: 24px;
                                height: 24px;
                                border-radius: 50%;
                                background-color: #6c757d;
                                border: 3px solid white;
                                box-shadow: 0 2px 8px rgba(0,0,0,0.5);
                              "></div>
                            `;
                          }
                        }}
                      />

                      {/* Badge de identificação */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '-8px',
                          right: '-8px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#6c757d',
                          border: '2px solid white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.4)'
                        }}
                      >
                        <span
                          style={{
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}
                        >
                          ◉
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${overlaySign.x_coordinate * 100}%`,
                        top: `${overlaySign.y_coordinate * 100}%`,
                        transform: 'translate(-50%, -50%)',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: '#6c757d',
                        border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                        pointerEvents: 'none',
                        zIndex: 10
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold'
                        }}
                      >
                        ◉
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="text-muted small mt-1 text-center">
              Coordenadas: X={overlaySign.x_coordinate.toFixed(3)}, Y=
              {overlaySign.y_coordinate.toFixed(3)}
            </div>
          </div>
        </Col>
      </Row>

      <hr />

      {/* Rodapé */}
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          <FeatherIcon icon="clock" size={12} className="me-1" />
          Cadastrado em:{' '}
          {new Date(overlaySign.created_at).toLocaleString('pt-BR')}
        </div>
        <div className="d-flex gap-2">
          {/* ✅ Botão "Ir para Projeto" - só aparece se hideProjectButton === false */}
          {!hideProjectButton && overlaySign.projectId && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                const projectUrl = `/app/video/watch?projectId=${overlaySign.projectId}&imageId=${overlaySign.project_image_id}&signId=${overlaySign.id}`;

                console.log('[OverlayModal] 🔗 Navegando para:', projectUrl, {
                  projectId: overlaySign.projectId,
                  imageId: overlaySign.project_image_id,
                  signId: overlaySign.id
                });

                window.open(projectUrl, '_blank');
                onHide();
              }}
              title="Abrir projeto de origem no frame correto"
            >
              <FeatherIcon icon="external-link" size={14} className="me-1" />
              Ir para Projeto
            </Button>
          )}
          <Button variant="secondary" onClick={onHide}>
            Fechar
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default SignalOverlayViewModal;
